import PropTypes from 'prop-types';
import React, { Fragment, useContext, useEffect } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';
import { LoadablePaint } from '@confluence/loadable';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { ArchiveSpacePortal } from '@confluence/archive-space/entry-points/ArchiveSpacePortal.ts';
import { DeleteSpacePortal } from '@confluence/delete-space';
import { LazySpaceSettingsForgeExtensionPortal } from '@confluence/space-settings-forge-extension';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { SpaceSettingsNavigationV2Portal } from '@confluence/space-settings';
import { SpaceTrashPortal } from '@confluence/space-trash';
import { SiteSettingsHelpPanelLink } from '@confluence/in-product-help/entry-points/SiteSettingsHelpPanelLink.ts';
import { BackupManagerButton } from '@confluence/in-product-help/entry-points/BackupManagerButton.ts';
import { getRouteData } from '@confluence/in-product-help/entry-points/inProductHelpUtil.ts';
import { PageStateContainer } from '@confluence/page-context';
import { PdfBannerPortal } from '@confluence/pdf-export/entry-points/PdfBanner.ts';

const LegacyAttachmentForgeActionsPortalLoader = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-LegacyAttachmentForgeActionsPortal" */ './LegacyAttachmentForgeActionsPortal.jsx'
			)
		).LegacyAttachmentForgeActionsPortal,
});

const i18n = defineMessages({
	importingTemplates: {
		id: 'standalone-navigation.importing.templates',
		defaultMessage: 'More about importing templates',
		description: 'Learn more link about importing templates',
	},
});

const LEGACY_ATTACHMENTS_PATH = '/pages/viewpageattachments.action';

const getLegacyAttachmentsSearch = ({ pathname, search, renderServerPlaceholder }) => {
	if (pathname?.includes(LEGACY_ATTACHMENTS_PATH)) {
		return search ?? '';
	}

	if (!renderServerPlaceholder && window.location.pathname.includes(LEGACY_ATTACHMENTS_PATH)) {
		return window.location.search;
	}

	return undefined;
};

const LegacyAttachmentForgeActionsPortal = ({ spaceKey, pathname, search }) => {
	const renderServerPlaceholder = useIsSSROrHydrating();
	const legacyAttachmentsSearch = getLegacyAttachmentsSearch({
		pathname,
		search,
		renderServerPlaceholder,
	});
	const contentId = legacyAttachmentsSearch
		? (new URLSearchParams(legacyAttachmentsSearch).get('pageId') ?? undefined)
		: undefined;

	if (legacyAttachmentsSearch == null || !fg('forge_attachment_display_conditions')) {
		return null;
	}

	return (
		<PageStateContainer contentId={contentId} spaceKey={spaceKey}>
			<LegacyAttachmentForgeActionsPortalLoader spaceKey={spaceKey} />
		</PageStateContainer>
	);
};

LegacyAttachmentForgeActionsPortal.propTypes = {
	spaceKey: PropTypes.string,
	pathname: PropTypes.string,
	search: PropTypes.string,
};

export const PortalsForLegacyPages = ({ spaceKey, isSpaceSettingsScreen }) => {
	const {
		location: { pathname, search },
	} = useContext(RoutesContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const spaceId = useSpaceId(spaceKey);

	useEffect(() => {
		const { section, screen } = getRouteData(pathname);

		// We only want analytics for IPH specific routes for now
		if (screen !== 'notTracked') {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: screen,
					attributes: {
						section,
					},
				},
			}).fire();
		}
	}, [pathname, createAnalyticsEvent, spaceId]);

	return (
		pathname && (
			<Fragment>
				{/* SpaceSettingsNavigationV2Portal renders on all legacy space settings pages
            since it encapsulates the space settings navigation */}
				{isSpaceSettingsScreen ? (
					<PageStateContainer spaceKey={spaceKey}>
						<SpaceSettingsNavigationV2Portal spaceKey={spaceKey} />
					</PageStateContainer>
				) : null}
				{pathname.includes('/spaces/archivespace.action') && (
					<ArchiveSpacePortal spaceKey={spaceKey} pathname={pathname} />
				)}
				{pathname.includes('/spaces/removespace.action') && (
					<DeleteSpacePortal spaceKey={spaceKey} pathname={pathname} />
				)}
				{pathname.includes('/pages/viewtrash.action') && <SpaceTrashPortal spaceKey={spaceKey} />}
				{pathname.includes('/templatePackage/configurePlugin.action') && (
					<SiteSettingsHelpPanelLink
						articleId="7p0QTJJI0VkoU49M4GzKH2"
						articleTopic="admin-import-templates"
						portalTargetId="site-settings-import"
						buttonContent={<FormattedMessage {...i18n.importingTemplates} />}
						pathname={pathname}
						routeGroup="site-settings"
						routeName="undefined"
					/>
				)}
				{pathname.includes('/plugins/servlet/ondemandbackupmanager') && (
					<BackupManagerButton pathname={pathname} />
				)}

				{pathname.includes('spaces/flyingpdf/pdfpageexport.action') && <PdfBannerPortal />}
				<LegacyAttachmentForgeActionsPortal
					spaceKey={spaceKey}
					pathname={pathname}
					search={search}
				/>

				{/* LazySpaceSettingsForgeExtensionPortal shows up in Integrations tab and can have many extensions and many urls */}
				<LazySpaceSettingsForgeExtensionPortal spaceKey={spaceKey} />
			</Fragment>
		)
	);
};

// needed for react-proptypes warning
PortalsForLegacyPages.propTypes = {
	spaceKey: PropTypes.string,
	isSpaceSettingsScreen: PropTypes.bool,
};
